import React, { HTMLAttributes, useMemo, useState } from 'react';

import { Table, DefaultRow } from 'common/Table';
import { usePicklistEditor } from 'domains/reporter/Template/usePicklist';
import Tooltip from 'common/ui/Tooltip';
import { fragmentToString } from 'domains/reporter/RichTextEditor/utils';
import { isMacro } from 'domains/reporter/RichTextEditor/plugins/macroPlaceholder/utils';
import type { Macro } from 'domains/reporter/RichTextEditor/plugins/macroPlaceholder/types';
import { Colors } from 'styles';
import type { ColumnDef, Row } from '@tanstack/react-table';
import type { TableRowProps } from 'common/Table';
import { Node } from 'domains/reporter/RichTextEditor/core';
import { rootTheme } from 'theme/rootTheme';
import { useSendModifyDictationMessage } from 'common/Recorder/useRecorder';

const NUM_PLACEHOLDER_ROWS = 5;
const PLACEHOLDER_NAME = '\u00A0';

type PicklistData = {
  empty?: boolean;
  name: Nullable<string>;
  text?: string | Macro | null;
  id?: string;
};

type PicklistOptionRowProps = TableRowProps<PicklistData> & {
  isSelected: boolean;
} & HTMLAttributes<HTMLDivElement>;

const PicklistOptionRow = ({ row, isSelected, children, ...props }: PicklistOptionRowProps) => {
  return (
    <DefaultRow
      css={`
        user-select: none;
        cursor: pointer;
        background-color: ${isSelected ? Colors.mainBlueHover : ' #000a12'};
        padding: 0px 12px;
        border-bottom: 1px solid ${Colors.gray4};
      `}
      data-testid={row.original.empty === true ? '' : 'picklist-option-row'}
      aria-selected={isSelected}
      row={row}
      {...props}
    >
      <Tooltip
        content={
          (row.original.text != null
            ? isMacro(row.original.text)
              ? fragmentToString(row.original.text as Node[])
              : row.original.text
            : null) as string
        }
      >
        <div css="width: 100%; display: flex;">{children}</div>
      </Tooltip>
    </DefaultRow>
  );
};

const IDRow = ({ row }: { row: Row<PicklistData> }) => {
  return (
    <div>
      {row.original.name !== PLACEHOLDER_NAME && row.original.empty !== true ? row.index + 1 : ''}
    </div>
  );
};

const EMPTY_ROW_DATA = { name: 'No picklist selected.', empty: true } as const;

export const PicklistOptionsViewer = (): React.ReactElement => {
  const { activePicklist, selectedPicklistOptionIds, selectPicklistItem } = usePicklistEditor();
  const sendModifyDictationMessage = useSendModifyDictationMessage();
  const [hoveredPicklistOptionIndex, setHoveredPicklistOptionIndex] = useState<number | null>(null);

  const picklistColumns: Array<ColumnDef<PicklistData>> = useMemo(
    () => [
      {
        header: '#',
        accessorFn: (data, index) => (data.name !== PLACEHOLDER_NAME ? index + 1 : ''),
        size: 2,
        height: 12,
        cell: ({ row }) => !(row.original.empty ?? false) && <IDRow row={row} />,
      },
      {
        header: 'Name',
        size: 15,
        cell: ({ row }) => (
          <div
            css={`
              white-space: normal;
            `}
          >
            {row.original.name}
          </div>
        ),
      },
    ],
    []
  );

  return (
    <Table<PicklistData>
      data-testid="picklist-options-viewer"
      css={`
        margin-top: 8px;
        background-color: ${Colors.gray2};
        border-radius: ${rootTheme.radius.sm}px;
        overflow: auto;

        .table-header {
          background-color: ${Colors.gray2};
          padding: 0px 16px;
          border-radius: ${rootTheme.radius.sm}px ${rootTheme.radius.sm}p 0px 0px;
          font-size: 13px;
          line-height: 24px;
          font-weight: 400;
          color: ${Colors.gray10};
        }
      `}
      columns={picklistColumns}
      data={
        activePicklist == null
          ? ([
              EMPTY_ROW_DATA,
              ...Array.from({ length: NUM_PLACEHOLDER_ROWS - 1 }, () => ({
                name: PLACEHOLDER_NAME,
                empty: true,
              })),
            ] as PicklistData[])
          : ([
              ...activePicklist.options,
              ...Array.from(
                { length: NUM_PLACEHOLDER_ROWS - activePicklist.options.length },
                () => ({
                  name: PLACEHOLDER_NAME,
                  empty: true,
                })
              ),
            ] as PicklistData[])
      }
      Row={(props) => {
        const isHovered =
          activePicklist != null &&
          hoveredPicklistOptionIndex != null &&
          hoveredPicklistOptionIndex < activePicklist.options.length &&
          props.row.index === hoveredPicklistOptionIndex;
        const isSelected =
          hoveredPicklistOptionIndex == null &&
          activePicklist != null &&
          selectedPicklistOptionIds[activePicklist.id] != null &&
          selectedPicklistOptionIds[activePicklist.id] === props.row.original.id;
        return (
          <PicklistOptionRow
            isSelected={isHovered || isSelected}
            onMouseEnter={() => setHoveredPicklistOptionIndex(props.row.index)}
            onMouseLeave={() => setHoveredPicklistOptionIndex(null)}
            onMouseDown={(e) => {
              // Don't steal the focus from the editor!
              e.preventDefault();

              if (props.row.original.empty === true) return;

              selectPicklistItem(props.row.index);
              sendModifyDictationMessage();
            }}
            css={{
              backgroundColor: '#000a12',
            }}
            {...props}
          />
        );
      }}
    />
  );
};
